// 작성 탭 분기 슬롯 탭 — 활성 경로 전개 + 라벨 규약(순수, DOM 미접촉).
//
// 오버레이(WriteCanvasBranchTabsOverlay)는 "위젯 DOM이 실제로 있는가"로 활성 경로를 최종 판정하지만,
// 그 판정에 넣을 **후보**는 트리에서 나와야 한다(DOM 순회로 활성/비활성을 가리려면 오버레이가 슬롯
// 구조를 다시 해석해야 한다). 그 전개와 라벨 규약을 순수 함수로 떼 두면 렌더를 띄우지 않고도
// 테스트로 고정할 수 있다.
//
// ⚠️ 한때(2026-08-31 오전) 이 모듈은 탭 바를 캔버스 왼쪽 여백 열에 세우던 시절의 **예약 폭**
// (`branchTabColumnWidth` → CanvasViewport `leftGutter`)까지 냈다. 배치가 노드 위로 돌아오면서
// 열 자체가 없어져 예약도 사라졌고(fit은 캔버스만 맞추면 된다), 고정폭 글꼴 기반 폭 추정도 함께
// 걷어냈다. 남은 건 전개·라벨뿐이다.

import type { BranchWidget, ComposerWidget } from '@safetyhub/shared';
import { activeIndexOf, truncate } from './chainOfBranches';

/**
 * 슬롯 탭 표시 라벨 — label 우선 → 조건 요약(비지 않은 condition) → '슬롯 N'(위젯 슬롯 폴백 관례).
 *
 * ⚠️ 본문(양식) 탭의 `slotLabel`(chainOfBranches)과 폴백이 다르다: 저쪽은 condition이 **undefined**면
 * `else`(문법상 마지막 가지)이고 여기는 `슬롯 N`이다. 위젯 슬롯에는 else 개념이 없기 때문이라
 * 통합하면 안 된다 — 모양·상호작용만 공유하고(BranchSlotTabBar) 라벨은 각자 계산해 넘긴다.
 * 다만 **자르는 규칙**(22자)에는 그런 차이가 없어 `truncate`를 함께 쓴다.
 */
export function slotTabLabel(
  slot: { label?: string; condition?: string },
  index: number,
): string {
  const label = slot.label?.trim();
  if (label) return truncate(label);
  const c = slot.condition?.trim();
  if (c) return truncate(c);
  return `슬롯 ${index + 1}`;
}

/**
 * branch 위젯의 활성 슬롯 index — 범위를 벗어난 저장값은 0으로 되돌린다(렌더가 0번을 그린다).
 * clamp 규칙은 본문 탭과 같아 `chainOfBranches.activeIndexOf`가 정본이다.
 */
export function activeSlotIndex(
  branch: BranchWidget,
  activeBranchSlots: Record<string, number>,
): number {
  return activeIndexOf(branch, activeBranchSlots);
}

/**
 * **활성 경로상** branch 위젯들(부모 → 자식 순). 비활성 슬롯의 children은 애초에 렌더되지 않으므로
 * 내려가지 않는다 — 오버레이가 DOM 존재로 하는 재귀를 트리에서 그대로 편 것이다.
 *
 * ⚠️ modalTrigger 안의 branch는 모달을 열지 않으면 실제로는 렌더되지 않는데 여기선 세어진다.
 * 오버레이 쪽이 DOM 존재로 한 번 더 거르므로 탭이 헛 뜨지는 않는다(후보를 넓게 잡는 방향이 안전).
 */
export function activeBranchWidgets(
  tree: ComposerWidget[],
  activeBranchSlots: Record<string, number>,
): BranchWidget[] {
  const out: BranchWidget[] = [];
  const walk = (nodes: ComposerWidget[]): void => {
    for (const w of nodes) {
      if (w.kind === 'group' || w.kind === 'modalTrigger' || w.kind === 'repeat') {
        walk(w.children);
      } else if (w.kind === 'branch') {
        out.push(w);
        // 활성 슬롯의 children만 — 비활성 슬롯 속 중첩 branch는 렌더되지 않는다.
        const slot = w.branches[activeSlotIndex(w, activeBranchSlots)];
        if (slot) walk(slot.children);
      }
    }
  };
  walk(tree);
  return out;
}
